import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getClasses, getTerms, getSubjects, getStudents,
  getSubjectRegistrationsByClass, saveSubjectRegistrations,
  getClassById, setClassSubjects,
} from '@/api/owner';
import { api } from '@/lib/api';
import { Check, X, Users, BookOpen, AlertCircle, AlertTriangle, Search, CheckCircle2, ChevronRight, Lock } from 'lucide-react';

/** Can students pick their own electives (on top of compulsory subjects), and how many subjects at most? */
function StudentChoiceCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['owner-settings'], queryFn: () => api.get('/school/settings').then((r) => r.data?.data) });
  const st = q.data?.settings ?? {};
  const [max, setMax] = useState<string>('');
  useEffect(() => { setMax(st.max_subjects ? String(st.max_subjects) : ''); }, [st.max_subjects]);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put('/school/settings', { settings: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner-settings'] }),
  });
  const on = st.students_choose_subjects !== false;
  if (q.data && q.data.editable === false) {
    return <p className="text-xs text-slate-600 bg-white rounded-xl border border-slate-200 px-4 py-3">Students {on ? 'can' : 'cannot'} add their own electives{on && st.max_subjects ? ` (up to ${st.max_subjects} subjects)` : ''}. The school owner sets this.</p>;
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1">
        <p className="font-semibold text-slate-900">Students choose their own electives</p>
        <p className="text-xs text-slate-500">Compulsory subjects are always included. When on, students add electives from their class list (or any school subject if the class has none) under My subjects, until the term's results are published.</p>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-slate-600">Max subjects</label>
        <input type="number" min={1} max={30} placeholder="No limit" value={max} onChange={(e) => setMax(e.target.value)}
          onBlur={() => save.mutate({ max_subjects: max ? Number(max) : null })} className="w-24 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
        <button onClick={() => save.mutate({ students_choose_subjects: !on })} disabled={save.isPending || q.isLoading}
          className={`relative w-12 h-7 rounded-full transition ${on ? 'bg-green-600' : 'bg-slate-300'}`} aria-label="Students choose their own electives">
          <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
      </div>
    </div>
  );
}

/* ───────────────────────── helpers ───────────────────────── */

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const fmtClass = (c: any) => (c?.name ?? '') + (c?.arm ? ' · ' + c.arm : '');
const studentName = (s: any) => [s?.first_name, s?.middle_name, s?.last_name].filter(Boolean).join(' ') || s?.name || `Student #${s?.id}`;
const errText = (e: any) => {
  const d = e?.response?.data;
  const first = d?.errors && (Object.values(d.errors).flat()[0] as string);
  return first || d?.message || e?.message || 'Save failed.';
};

/** The students endpoint caps per_page at 100, so walk every page. */
async function fetchAllStudents(classId: number): Promise<any[]> {
  const out: any[] = [];
  for (let page = 1; page <= 20; page++) {
    const r: any = await getStudents({ class_id: classId, status: 'active', per_page: 100, page });
    const body = r.data ?? r;
    out.push(...arr<any>(body));
    const last = body?.meta?.last_page ?? 1;
    if (page >= last) break;
  }
  return out;
}

type Mode = 'class' | 'student';
interface Offer { offered: boolean; compulsory: boolean; pass_mark: number }

/* ───────────────────────── page ───────────────────────── */

export function SubjectRegistrationPage() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>('class');
  const [classId, setClassId] = useState<number | ''>('');
  const [termId, setTermId] = useState<number | ''>('');
  const [studentId, setStudentId] = useState<number | ''>('');
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ names: string[]; count: number } | null>(null);

  // Class-mode draft: subject_id -> offering
  const [offers, setOffers] = useState<Map<number, Offer>>(new Map());
  // Student-mode draft
  const [picked, setPicked] = useState<Set<number>>(new Set());

  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses({ per_page: 200 }).then((r: any) => r.data?.data ?? r.data) });
  const termsQ = useQuery({ queryKey: ['owner-terms'], queryFn: () => getTerms().then((r: any) => r.data?.data ?? r.data) });
  const subjectsQ = useQuery({ queryKey: ['owner-subjects'], queryFn: () => getSubjects({ per_page: 500 }).then((r: any) => r.data?.data ?? r.data) });

  const classes = arr<any>(classesQ.data);
  const terms = arr<any>(termsQ.data);
  const subjects = arr<any>(subjectsQ.data).filter((s) => s.is_active !== false);

  useEffect(() => {
    if (!termId && terms.length > 0) {
      const current = terms.find((t: any) => t.is_current) ?? terms[0];
      if (current) setTermId(current.id);
    }
  }, [terms, termId]);

  // Students: needed in BOTH modes (class mode registers every student).
  const studentsQ = useQuery({
    queryKey: ['students-in-class', classId],
    queryFn: () => fetchAllStudents(Number(classId)),
    enabled: !!classId,
  });
  const students = studentsQ.data ?? [];

  // What the class offers (class_subjects).
  const classQ = useQuery({
    queryKey: ['owner-class', classId],
    queryFn: () => getClassById(Number(classId)).then((r: any) => r.data?.data ?? r.data),
    enabled: !!classId,
  });
  const classSubjects = arr<any>(classQ.data?.subjects);

  const existingQ = useQuery({
    queryKey: ['subject-registrations', classId, termId],
    queryFn: () => getSubjectRegistrationsByClass(Number(classId), Number(termId)).then((r: any) => r.data?.data ?? r.data),
    enabled: !!classId && !!termId,
  });
  const existing = arr<any>(existingQ.data);

  /* ── derived ── */
  const regCountBySubject = useMemo(() => {
    const m = new Map<number, number>();
    existing.forEach((r) => m.set(Number(r.subject_id), (m.get(Number(r.subject_id)) ?? 0) + 1));
    return m;
  }, [existing]);

  const regsByStudent = useMemo(() => {
    const m = new Map<number, Set<number>>();
    existing.forEach((r) => {
      const k = Number(r.student_id);
      m.set(k, (m.get(k) ?? new Set()).add(Number(r.subject_id)));
    });
    return m;
  }, [existing]);

  const savedOffers = useMemo(() => {
    const m = new Map<number, Offer>();
    classSubjects.forEach((cs) => m.set(Number(cs.id ?? cs.subject_id), { offered: true, compulsory: cs.is_compulsory !== false && cs.is_compulsory !== 0, pass_mark: Number(cs.pass_mark ?? 40) }));
    return m;
  }, [classSubjects]);

  const compulsoryIds = useMemo(() => [...savedOffers.entries()].filter(([, o]) => o.compulsory).map(([id]) => id), [savedOffers]);

  // Seed class-mode draft from what the class offers.
  useEffect(() => { setOffers(new Map(savedOffers)); }, [savedOffers, classId]);

  // Seed student-mode draft from the student's current registrations (or the compulsory list if none yet).
  useEffect(() => {
    if (mode !== 'student' || !studentId) return;
    const mine = regsByStudent.get(Number(studentId));
    setPicked(new Set(mine && mine.size ? mine : compulsoryIds));
  }, [mode, studentId, regsByStudent, compulsoryIds]);

  useEffect(() => { setNotice(null); }, [mode, classId, termId, studentId]);

  const offerOf = (id: number): Offer => offers.get(id) ?? { offered: false, compulsory: true, pass_mark: 40 };
  const setOffer = (id: number, patch: Partial<Offer>) => setOffers((m) => new Map(m).set(id, { ...offerOf(id), ...patch }));

  const visibleSubjects = subjects.filter((s) => !search.trim() || `${s.name} ${s.code ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()));
  const offeredIds = [...offers.entries()].filter(([, o]) => o.offered).map(([id]) => id);

  const classDirty = useMemo(() => {
    const keys = new Set([...offers.keys(), ...savedOffers.keys()]);
    for (const k of keys) {
      const a = offers.get(k); const b = savedOffers.get(k);
      if (!!a?.offered !== !!b?.offered) return true;
      if (a?.offered && (a.compulsory !== b?.compulsory || a.pass_mark !== b?.pass_mark)) return true;
    }
    return false;
  }, [offers, savedOffers]);

  // Students missing at least one compulsory subject this term.
  const missing = students.filter((s) => {
    const mine = regsByStudent.get(Number(s.id)) ?? new Set();
    return compulsoryIds.some((id) => !mine.has(id));
  });

  /* ── save: whole class ── */
  const saveClassM = useMutation({
    mutationFn: async () => {
      const cid = Number(classId); const tid = Number(termId);
      const ids = students.map((s) => Number(s.id));
      // 1. Save what the class offers.
      await setClassSubjects(cid, offeredIds.map((id) => ({ subject_id: id, is_compulsory: offerOf(id).compulsory, pass_mark: offerOf(id).pass_mark })));
      if (ids.length === 0) return { added: 0, removed: 0, noStudents: true };
      let added = 0; let removed = 0;
      // 2. Register every student for every compulsory subject (idempotent; also catches students who joined later).
      for (const sid of offeredIds.filter((id) => offerOf(id).compulsory)) {
        for (let i = 0; i < ids.length; i += 500) {
          const r: any = await saveSubjectRegistrations({ term_id: tid, subject_id: sid, student_ids: ids.slice(i, i + 500) });
          added += Number(r.data?.data?.changed ?? 0);
        }
      }
      // 3. Subjects no longer offered: remove this term's registrations for the class.
      for (const sid of [...regCountBySubject.keys()].filter((id) => !offerOf(id).offered)) {
        for (let i = 0; i < ids.length; i += 500) {
          const r: any = await saveSubjectRegistrations({ term_id: tid, subject_id: sid, student_ids: ids.slice(i, i + 500), remove: true });
          removed += Number(r.data?.data?.changed ?? 0);
        }
      }
      return { added, removed, noStudents: false };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['subject-registrations'] });
      qc.invalidateQueries({ queryKey: ['owner-class', classId] });
      setNotice({ tone: 'ok', text: r.noStudents
        ? 'Class subjects saved. There are no active students in this class yet.'
        : `Saved. ${r.added} new registration(s)${r.removed ? `, ${r.removed} removed` : ''}.` });
    },
    onError: (e) => { qc.invalidateQueries({ queryKey: ['subject-registrations'] }); setNotice({ tone: 'err', text: errText(e) }); },
  });

  const onSaveClass = () => {
    const dropping = [...regCountBySubject.keys()].filter((id) => !offerOf(id).offered);
    if (dropping.length) {
      setConfirmRemove({
        names: dropping.map((id) => subjects.find((s) => Number(s.id) === id)?.name ?? `Subject #${id}`),
        count: dropping.reduce((a, id) => a + (regCountBySubject.get(id) ?? 0), 0),
      });
      return;
    }
    saveClassM.mutate();
  };

  /* ── save: one student ── */
  const saveStudentM = useMutation({
    mutationFn: () => saveSubjectRegistrations({ term_id: Number(termId), student_id: Number(studentId), subject_ids: [...picked] }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['subject-registrations'] }); setNotice({ tone: 'ok', text: 'Student’s subjects saved.' }); },
    onError: (e) => setNotice({ tone: 'err', text: errText(e) }),
  });

  const togglePick = (id: number) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const openStudent = (id: number) => { setMode('student'); setStudentId(id); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const ready = !!classId && !!termId;
  const loading = studentsQ.isLoading || classQ.isLoading || existingQ.isLoading;
  const currentStudent = students.find((s) => Number(s.id) === Number(studentId));
  const studentOffered = subjects.filter((s) => savedOffers.has(Number(s.id)));
  const studentOther = subjects.filter((s) => !savedOffers.has(Number(s.id)));

  return (
    <div className="max-w-[1400px] mx-auto p-4 sm:p-6 space-y-5 pb-28 md:pb-10">
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Academics</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Subject Registration</h1>
        <p className="text-slate-500 text-sm mt-1">Choose the subjects each class offers, register students for the term, and enrol individual students in electives.</p>
      </div>

      <StudentChoiceCard />

      {/* MODE */}
      <div className="grid grid-cols-2 sm:inline-flex bg-slate-100 rounded-lg p-1 w-full sm:w-auto">
        {([['class', Users, 'Whole class'], ['student', BookOpen, 'Individual student']] as const).map(([m, Icon, label]) => (
          <button key={m} onClick={() => { setMode(m); if (m === 'class') setStudentId(''); }}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition flex items-center justify-center gap-1.5 ${mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* FILTERS */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Class *">
            <select value={classId} onChange={(e) => { setClassId(e.target.value ? Number(e.target.value) : ''); setStudentId(''); }} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white">
              <option value="">Select class…</option>
              {classes.map((c: any) => <option key={c.id} value={c.id}>{fmtClass(c)}</option>)}
            </select>
          </Field>
          <Field label="Term *">
            <select value={termId} onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white">
              <option value="">Select term…</option>
              {terms.map((t: any) => <option key={t.id} value={t.id}>{t.name}{t.session?.name ? ` · ${t.session.name}` : ''}{t.is_current ? ' (current)' : ''}</option>)}
            </select>
          </Field>
          {mode === 'student' && (
            <Field label="Student *">
              <select value={studentId} onChange={(e) => setStudentId(e.target.value ? Number(e.target.value) : '')} disabled={!classId} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white disabled:opacity-50">
                <option value="">{studentsQ.isLoading ? 'Loading students…' : students.length ? 'Select student…' : 'No students in this class'}</option>
                {students.map((s: any) => <option key={s.id} value={s.id}>{studentName(s)}{s.admission_no ? ` (${s.admission_no})` : ''}</option>)}
              </select>
            </Field>
          )}
        </div>
      </div>

      {!ready && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900 flex gap-2"><AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> Select a class and a term to see and edit registered subjects.</div>
      )}

      {notice && (
        <div className={`flex items-start justify-between gap-3 rounded-lg px-4 py-3 text-sm border ${notice.tone === 'ok' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
          <span className="flex gap-2">{notice.tone === 'ok' ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}{notice.text}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}

      {ready && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Active students" value={students.length} />
          <Stat label="Subjects offered" value={savedOffers.size} />
          <Stat label="Compulsory" value={compulsoryIds.length} />
          <Stat label="Missing compulsory" value={missing.length} warn />
        </div>
      )}

      {/* ───── WHOLE CLASS ───── */}
      {ready && mode === 'class' && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 sm:px-5 py-3 border-b border-slate-100 bg-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-slate-900">Subjects offered by {fmtClass(classes.find((c) => Number(c.id) === Number(classId)))}</p>
                <p className="text-xs text-slate-500">{offeredIds.length} offered · compulsory subjects are registered for every student</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative flex-1 sm:w-56">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search subjects" className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg text-sm" />
                </div>
                <button onClick={() => setOffers((m) => { const n = new Map(m); const all = visibleSubjects.every((s) => offerOf(Number(s.id)).offered); visibleSubjects.forEach((s) => n.set(Number(s.id), { ...offerOf(Number(s.id)), offered: !all })); return n; })}
                  className="text-xs font-semibold text-slate-700 hover:text-slate-900 underline whitespace-nowrap">
                  {visibleSubjects.length > 0 && visibleSubjects.every((s) => offerOf(Number(s.id)).offered) ? 'Clear all' : 'Select all'}
                </button>
              </div>
            </div>

            {loading || subjectsQ.isLoading ? (
              <p className="p-6 text-center text-sm text-slate-500">Loading…</p>
            ) : subjects.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-500">No subjects exist yet. Add subjects on Academics → Overview first.</p>
            ) : (
              <ul className="divide-y divide-slate-100 max-h-[560px] overflow-y-auto">
                {visibleSubjects.map((s: any) => {
                  const id = Number(s.id); const o = offerOf(id);
                  const n = regCountBySubject.get(id) ?? 0;
                  const full = students.length > 0 && n >= students.length;
                  return (
                    <li key={id} className={`px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3 ${o.offered ? '' : 'opacity-80'}`}>
                      <button type="button" onClick={() => setOffer(id, { offered: !o.offered })} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                        <Box checked={o.offered} />
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-slate-900">{s.name}{s.code && <span className="ml-2 text-xs font-normal text-slate-400">{s.code}</span>}</span>
                          <span className={`block text-xs ${n === 0 ? 'text-slate-400' : full ? 'text-green-700' : o.offered && o.compulsory ? 'text-amber-600' : 'text-slate-500'}`}>
                            {n === 0 ? 'No students registered' : `${n} of ${students.length} students registered`}
                          </span>
                        </span>
                      </button>
                      {o.offered && (
                        <div className="flex items-center gap-2 pl-8 sm:pl-0">
                          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-xs font-semibold">
                            <button type="button" onClick={() => setOffer(id, { compulsory: true })} className={`px-2.5 py-1 rounded-md ${o.compulsory ? 'bg-[#0f172a] text-white' : 'text-slate-600'}`}>Compulsory</button>
                            <button type="button" onClick={() => setOffer(id, { compulsory: false })} className={`px-2.5 py-1 rounded-md ${!o.compulsory ? 'bg-amber-500 text-white' : 'text-slate-600'}`}>Elective</button>
                          </div>
                          <label className="flex items-center gap-1 text-xs text-slate-500">
                            Pass
                            <input type="number" min={0} max={100} value={o.pass_mark} onChange={(e) => setOffer(id, { pass_mark: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} className="w-14 border border-slate-300 rounded-md px-1.5 py-1 text-xs text-slate-900" />
                          </label>
                        </div>
                      )}
                    </li>
                  );
                })}
                {visibleSubjects.length === 0 && <li className="p-6 text-center text-sm text-slate-500">No subjects match “{search}”.</li>}
              </ul>
            )}

            <SaveBar
              hint={<>Compulsory subjects are registered for all {students.length} active student(s). Electives are chosen per student.</>}
              label={classDirty ? 'Save & register' : 'Re-sync registrations'}
              disabled={offeredIds.length === 0 || saveClassM.isPending}
              pending={saveClassM.isPending}
              onSave={onSaveClass}
            />
          </div>

          {/* Per-student overview */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-slate-900">Students this term</p>
                <p className="text-xs text-slate-500">Tap a student to change their electives.</p>
              </div>
              {missing.length > 0 && <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">{missing.length} missing compulsory</span>}
            </div>
            {studentsQ.isLoading ? <p className="p-6 text-center text-sm text-slate-500">Loading students…</p>
              : students.length === 0 ? <p className="p-6 text-center text-sm text-slate-500">No active students in this class.</p>
              : (
                <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                  {students.map((s) => {
                    const mine = regsByStudent.get(Number(s.id)) ?? new Set<number>();
                    const lack = compulsoryIds.filter((id) => !mine.has(id)).length;
                    const electives = [...mine].filter((id) => savedOffers.get(id) && !savedOffers.get(id)!.compulsory).length;
                    return (
                      <li key={s.id}>
                        <button onClick={() => openStudent(Number(s.id))} className="w-full px-4 sm:px-5 py-3 flex items-center gap-3 text-left hover:bg-slate-50">
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-semibold text-slate-900 truncate">{studentName(s)}</span>
                            <span className="block text-xs text-slate-500">{s.admission_no}</span>
                          </span>
                          <span className="text-right shrink-0">
                            <span className="block text-sm font-bold text-slate-900">{mine.size} <span className="font-normal text-slate-500">subjects</span></span>
                            <span className={`block text-xs ${lack ? 'text-amber-600' : 'text-slate-400'}`}>{lack ? `${lack} compulsory missing` : `${electives} elective${electives === 1 ? '' : 's'}`}</span>
                          </span>
                          <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
          </div>
        </>
      )}

      {/* ───── INDIVIDUAL STUDENT ───── */}
      {ready && mode === 'student' && studentId && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-900 truncate">{studentName(currentStudent)}</p>
              <p className="text-xs text-slate-500">{picked.size} subject(s) selected{regsByStudent.get(Number(studentId))?.size ? '' : ' · not registered yet this term'}</p>
            </div>
            <button onClick={() => setPicked(new Set([...picked, ...studentOffered.map((s) => Number(s.id))]))} className="text-xs font-semibold text-slate-700 underline whitespace-nowrap">Add all class subjects</button>
          </div>

          {savedOffers.size === 0 && (
            <p className="px-4 sm:px-5 py-3 text-xs text-amber-700 bg-amber-50 border-b border-amber-100 flex gap-1.5"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />This class has no subject list yet. Set it up under “Whole class” so compulsory subjects and electives are clear.</p>
          )}

          <SubjectGroup title="Class subjects" items={studentOffered} picked={picked} onToggle={togglePick} offers={savedOffers} />
          {studentOther.length > 0 && <SubjectGroup title="Other subjects (not offered by this class)" items={studentOther} picked={picked} onToggle={togglePick} offers={savedOffers} />}

          <SaveBar
            hint={<>Replaces this student’s subjects for the selected term.{compulsoryIds.some((id) => !picked.has(id)) && <span className="text-amber-700"> Some compulsory subjects are unticked.</span>}</>}
            label="Save student’s subjects"
            disabled={picked.size === 0 || saveStudentM.isPending}
            pending={saveStudentM.isPending}
            onSave={() => saveStudentM.mutate()}
          />
        </div>
      )}
      {ready && mode === 'student' && !studentId && (
        <p className="text-sm text-slate-500 bg-white border border-dashed border-slate-300 rounded-xl p-6 text-center">Pick a student above to manage their subjects.</p>
      )}

      {/* Confirm removal */}
      {confirmRemove && (
        <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setConfirmRemove(null)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-11 h-11 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-3"><AlertTriangle className="w-5 h-5" /></div>
            <h3 className="font-bold text-slate-900 text-lg">Remove subjects from this class?</h3>
            <p className="text-sm text-slate-600 mt-1">{confirmRemove.names.join(', ')} will no longer be offered. {confirmRemove.count} student registration(s) for this term will be removed, so those students drop off that subject’s score sheet and results. Scores already entered are kept.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setConfirmRemove(null)} className="flex-1 py-2.5 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700">Cancel</button>
              <button onClick={() => { setConfirmRemove(null); saveClassM.mutate(); }} className="flex-1 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold">Remove & save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── pieces ───────────────────────── */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>{children}</div>;
}

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 leading-tight">{label}</p>
      <p className={`text-2xl font-extrabold mt-1 ${warn && value > 0 ? 'text-amber-600' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

function Box({ checked }: { checked: boolean }) {
  return (
    <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${checked ? 'bg-[#0f172a] border-[#0f172a]' : 'bg-white border-slate-300'}`}>
      {checked && <Check className="w-3 h-3 text-white" />}
    </span>
  );
}

function SubjectGroup({ title, items, picked, onToggle, offers }: { title: string; items: any[]; picked: Set<number>; onToggle: (id: number) => void; offers: Map<number, Offer> }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="px-4 sm:px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-white border-b border-slate-100">{title}</p>
      <ul className="divide-y divide-slate-100">
        {items.map((s) => {
          const id = Number(s.id); const o = offers.get(id);
          return (
            <li key={id}>
              <button type="button" onClick={() => onToggle(id)} className="w-full flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-slate-50 text-left">
                <Box checked={picked.has(id)} />
                <span className="flex-1 min-w-0 text-sm font-semibold text-slate-900">{s.name}</span>
                {o && (o.compulsory
                  ? <span className="text-[11px] font-semibold text-slate-700 bg-slate-100 rounded-full px-2 py-0.5 flex items-center gap-1"><Lock className="w-3 h-3" />Compulsory</span>
                  : <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Elective</span>)}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Sticky on phones so Save is always reachable. */
function SaveBar({ hint, label, disabled, pending, onSave }: { hint: React.ReactNode; label: string; disabled: boolean; pending: boolean; onSave: () => void }) {
  return (
    <div className="sticky bottom-0 z-10 px-4 sm:px-5 py-3 border-t border-slate-200 bg-slate-50/95 backdrop-blur flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <p className="text-xs text-slate-600 flex gap-1.5"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>{hint}</span></p>
      <button disabled={disabled} onClick={onSave} className="w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 disabled:opacity-50 shrink-0">
        {pending ? 'Saving…' : label}
      </button>
    </div>
  );
}
